import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef } from "react"
import {
  AudioPlayerButton,
  AudioPlayerProgress,
  AudioPlayerProvider,
  AudioPlayerSpeed,
  AudioPlayerTime,
  useAudioPlayer,
} from "@ui/audio-player"
import { cn } from "@ui/utils"

interface RecordingAudioPlayerProps {
  src: string
  durationSeconds?: number
  onTimeUpdate?: (event: Event) => void
  className?: string
  rightControls?: ReactNode
  initialTimeSeconds?: number
  autoPlayOnMount?: boolean
}

interface AudioPlayerInnerProps {
  src: string
  durationSeconds?: number
  onTimeUpdate?: (event: Event) => void
  className?: string
  rightControls?: ReactNode
  initialTimeSeconds?: number
  autoPlayOnMount?: boolean
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

const AudioPlayerInner = forwardRef<HTMLAudioElement, AudioPlayerInnerProps>(
  ({ src, durationSeconds, onTimeUpdate, className, rightControls, initialTimeSeconds, autoPlayOnMount }, ref) => {
    const player = useAudioPlayer()
    const hasSetSource = useRef(false)
    const hasInitializedPlayback = useRef(false)

    // Expose the internal audio element ref to the parent
    useImperativeHandle(ref, () => player.ref.current as HTMLAudioElement, [player.ref])

    // Set the audio source directly on the element (only once)
    useEffect(() => {
      const audio = player.ref.current
      if (audio && !hasSetSource.current) {
        audio.src = src
        audio.load()
        hasSetSource.current = true
      }
    }, [src, player.ref])

    // Sync time updates to the parent callback via native event listener
    useEffect(() => {
      const audio = player.ref.current
      if (!audio || !onTimeUpdate) return

      audio.addEventListener("timeupdate", onTimeUpdate)
      return () => audio.removeEventListener("timeupdate", onTimeUpdate)
    }, [onTimeUpdate, player.ref])

    // Keep collapsed video->audio transitions aligned with previous playback state.
    useEffect(() => {
      const audio = player.ref.current
      if (!audio || hasInitializedPlayback.current) return

      if (initialTimeSeconds != null && Number.isFinite(initialTimeSeconds) && initialTimeSeconds > 0) {
        audio.currentTime = initialTimeSeconds
      }

      if (autoPlayOnMount) {
        void audio.play().catch(() => {})
      }

      hasInitializedPlayback.current = true
    }, [autoPlayOnMount, initialTimeSeconds, player.ref])

    // Use backend duration if available, otherwise fall back to player duration
    const displayDuration =
      durationSeconds != null
        ? formatDuration(durationSeconds)
        : player.duration != null && Number.isFinite(player.duration)
          ? formatDuration(player.duration)
          : "--:--"

    return (
      <div className={cn("bg-muted/50 flex flex-col gap-3 rounded-lg p-4", className)}>
        <div className="flex items-center gap-3">
          <AudioPlayerButton variant="outline" size="icon" className="size-10 shrink-0 rounded-full" />

          <div className="flex flex-1 items-center gap-2">
            <AudioPlayerTime className="w-12 text-xs" />
            <AudioPlayerProgress className="flex-1 cursor-pointer" maxDuration={durationSeconds} />
            <span className="text-muted-foreground w-12 text-right text-xs tabular-nums">{displayDuration}</span>
          </div>

          <div className="flex items-center gap-1">
            <AudioPlayerSpeed speeds={[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]} />
            {rightControls}
          </div>
        </div>
      </div>
    )
  }
)

AudioPlayerInner.displayName = "AudioPlayerInner"

const RecordingAudioPlayer = forwardRef<HTMLAudioElement, RecordingAudioPlayerProps>((props, ref) => {
  return (
    <AudioPlayerProvider>
      <AudioPlayerInner ref={ref} {...props} />
    </AudioPlayerProvider>
  )
})

RecordingAudioPlayer.displayName = "RecordingAudioPlayer"
export default RecordingAudioPlayer
